// app/src/client/components/AppSidebar/SidebarBottomSection.tsx
import { useRef, useState } from "react";
import { type User as UserEntity } from "wasp/entities";
import { logout } from "wasp/client/auth";
import { Link as WaspRouterLink } from "wasp/client/router";
import { Building2, Check, Layers, Leaf, Lock, LogOut, Moon, Sparkles, Sun, Sunrise } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Button } from "../ui/button";
import { cn } from "../../utils";
import useColorMode, { type ColorMode } from "../../hooks/useColorMode";
import { THEMES, getTheme, type ThemeMeta } from "../../theme/themes";
import { useLanguageContext, Language } from "../../contexts/LanguageContext";
import useTranslation from "../../hooks/useTranslation";
import { userMenuItems } from "../../../user/constants";
import { canUpgrade } from "../../../payment/plans";
import PlanSelectionModal from "../../../dashboard/PlanSelectionModal";
import { useUiMode } from "../../../lite/mode/useUiMode";

interface SidebarBottomSectionProps {
  isCollapsed: boolean;
  user: Partial<UserEntity> & { isAdmin?: boolean };
  /**
   * Reports whether any dropdown in this section is open. The parent keeps the
   * (hover-collapsing) rail expanded while a menu is open, so the menu's
   * trigger isn't torn out from under it when the cursor leaves the <aside>.
   */
  onMenuOpenChange?: (open: boolean) => void;
  /**
   * Mode Lite : la langue et le Discord tiennent sur une seule ligne, en
   * icônes seules avec info-bulle. Sans effet quand la section est repliée.
   */
  compactUtilities?: boolean;
}

function applyThemeTransition(e: React.MouseEvent, apply: () => void) {
  // Always apply the change first — correctness must never depend on the
  // View Transition API succeeding. The circular reveal is pure cosmetics.
  apply();
  if (!("startViewTransition" in document)) return;
  try {
    const x = e.clientX;
    const y = e.clientY;
    const maxRadius = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y));
    const transition = (document as any).startViewTransition(() => {});
    transition.ready
      .then(() => {
        document.documentElement.animate(
          { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${maxRadius}px at ${x}px ${y}px)`] },
          { duration: 400, easing: "ease-in-out", pseudoElement: "::view-transition-new(root)" },
        );
      })
      .catch(() => {});
  } catch {
    /* transition unsupported / interrupted — the theme already changed */
  }
}

const ICONS: Record<ThemeMeta["icon"], React.ElementType> = {
  Moon, Sun, Leaf, Sunrise, Building2,
};

function DiscordIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.043.03.056a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
  );
}

export function SidebarBottomSection({ isCollapsed, user, onMenuOpenChange, compactUtilities = false }: SidebarBottomSectionProps) {
  const [colorMode, setColorMode] = useColorMode();
  const { language, setLanguage } = useLanguageContext();
  const t = useTranslation();
  const uiMode = useUiMode();

  // Upgrade CTA: shown to anyone below the top plan (Free / Pro / Pro Annuel).
  const [showPlanModal, setShowPlanModal] = useState(false);
  const showUpgrade = canUpgrade(user.subscriptionPlan);

  // Track how many of the section's dropdowns are open. Reporting a single
  // boolean per-menu would race when one closes as another opens; a counter
  // collapses cleanly to "is anything open".
  const openCount = useRef(0);
  const reportOpen = (open: boolean) => {
    openCount.current = Math.max(0, openCount.current + (open ? 1 : -1));
    onMenuOpenChange?.(openCount.current > 0);
  };

  const tp = t.themePicker as Record<string, string>;
  const themes = THEMES.map((th) => ({
    value: th.id,
    label: tp[th.labelKey] ?? th.id,
    icon: ICONS[th.icon],
    badge: tp[th.badgeKey] ?? "",
    adminOnly: !!th.adminOnly,
  }));

  const ActiveIcon = ICONS[getTheme(colorMode).icon] ?? Moon;

  // Langue + Discord sur une ligne (Lite) : icônes seules, donc info-bulle aussi.
  const utilitiesRow = compactUtilities && !isCollapsed;

  const wrapTooltip = (content: React.ReactNode, label: string, iconOnly = isCollapsed) => {
    if (!iconOnly) return <>{content}</>;
    return (
      <Tooltip delayDuration={0}>
        {/* The trigger wraps a <span>, never the control itself. Pointing `asChild`
            at a DropdownMenuTrigger that is already `asChild` makes the two compete
            for the same button: the tooltip wins the ref and the menu stops opening.
            The span must be a real box: `display:contents` produces no rect, so Radix
            falls back to x=0 and the bubble lands on top of the rail. */}
        <TooltipTrigger asChild>
          <span className={cn("block", isCollapsed ? "w-fit" : "flex-1")}>{content}</span>
        </TooltipTrigger>
        {/* The control is narrower than its container, so "right of the button" is
            still inside it: centred at 40px wide it ends at 50px, the rail at 60px.
            Offset past that 10px gap so the bubble clears the edge instead of
            sitting over it. */}
        <TooltipContent side={isCollapsed ? "right" : "top"} sideOffset={isCollapsed ? 14 : 6} className="whitespace-nowrap">
          {label}
        </TooltipContent>
      </Tooltip>
    );
  };

  // Replie, la section s'aligne sur la colonne d'icones du rail : les controles
  // sont des boites de 40px centrees, comme les modules au-dessus. Le padding
  // horizontal est retire dans cet etat, sinon il decale les boutons de 8px vers
  // la droite et l'axe des icones se casse a mi-hauteur du rail.
  const CONTROL = isCollapsed
    ? "h-10 w-10 justify-center"
    : "h-8 w-full px-2";
  // Ligne compacte (Lite) : deux boîtes de même largeur, icône centrée.
  const UTILITY = utilitiesRow ? "h-8 w-full justify-center" : CONTROL;
  const utilityIconOnly = isCollapsed || utilitiesRow;

  const languageControl = wrapTooltip(
        <DropdownMenu onOpenChange={reportOpen}>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                "flex items-center gap-2 rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground",
                UTILITY,
              )}
              aria-label="Switch language"
            >
              <span className="text-base shrink-0">{language === "fr" ? "🇫🇷" : "🇬🇧"}</span>
              {!utilityIconOnly && (
                <span className="text-sm">{language === "fr" ? "Français" : "English"}</span>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="end">
            {(["fr", "en"] as Language[]).map((code) => (
              <DropdownMenuItem
                key={code}
                onClick={() => setLanguage(code)}
                className={cn("flex items-center gap-2 cursor-pointer", language === code && "bg-accent")}
              >
                <span className="text-base">{code === "fr" ? "🇫🇷" : "🇬🇧"}</span>
                <span>{code === "fr" ? "Français" : "English"}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>,
        language === "fr" ? "Français" : "English",
        utilityIconOnly,
      );

  const discordControl = wrapTooltip(
    <a
      href="https://discord.gg/jnrfKHzUVp"
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "flex items-center gap-2 rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground",
        UTILITY,
      )}
      aria-label="Discord"
    >
      <DiscordIcon className="h-4 w-4 shrink-0" />
      {!utilityIconOnly && <span className="text-sm">Discord</span>}
    </a>,
    "Discord",
    utilityIconOnly,
  );

  return (
    <div className={cn("flex flex-col gap-1 pb-3", isCollapsed ? "items-center px-0" : "px-2")}>
      {utilitiesRow ? (
        // Le shell Lite n'a pas le TooltipProvider du rail Advanced : la ligne
        // porte le sien, sinon Tooltip jette au rendu et la page entière tombe.
        <TooltipProvider delayDuration={0}>
          <div className="flex items-center gap-1">
            {languageControl}
            {discordControl}
          </div>
        </TooltipProvider>
      ) : (
        <>
          {languageControl}
          {discordControl}
        </>
      )}

      {/* Theme picker */}
      {wrapTooltip(
        <DropdownMenu onOpenChange={reportOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t.themePicker.switchTo}
              className={cn(
                "relative flex items-center gap-2 rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground",
                CONTROL,
              )}
            >
              <ActiveIcon className="h-4 w-4 shrink-0" aria-hidden />
              {!isCollapsed && (
                <span className="text-sm">
                  {themes.find((th) => th.value === colorMode)?.label ?? t.themePicker.label}
                </span>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side={isCollapsed ? "right" : "top"} align="end" className="w-48">
            {themes.map((theme) => {
              const Icon = theme.icon;
              const isActive = colorMode === theme.value;
              const isLocked = theme.adminOnly && !user.isAdmin;
              return (
                <DropdownMenuItem
                  key={theme.value}
                  disabled={isLocked}
                  className={cn(
                    "flex items-center gap-2",
                    isLocked ? "cursor-default opacity-50" : "cursor-pointer",
                    isActive && "bg-accent",
                  )}
                  onClick={isLocked ? undefined : (e) =>
                    applyThemeTransition(e, () => setColorMode(theme.value))
                  }
                >
                  <Icon className="size-4 shrink-0" />
                  <span className="flex-1 text-sm">{theme.label}</span>
                  {isLocked ? (
                    <span className="text-[10px] rounded px-1 py-0.5 bg-muted text-muted-foreground border border-border/50">
                      {t.themePicker.comingSoon}
                    </span>
                  ) : (
                    <>
                      <span className="text-[10px] opacity-50">{theme.badge}</span>
                      {isActive && <Check className="size-3 ml-1" />}
                    </>
                  )}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>,
        t.themePicker.switchTo,
      )}

      {/* User dropdown */}
      <DropdownMenu onOpenChange={reportOpen}>
        <DropdownMenuTrigger asChild>
          <button
            className={cn(
              "flex items-center gap-2 rounded-lg text-sm font-medium text-foreground/70 transition-colors hover:bg-accent hover:text-foreground",
              // Meme boite de 40px que les autres controles replies : l'avatar
              // faisait deja 40px de large mais gardait un padding vertical qui
              // le rendait plus haut que ses voisins.
              isCollapsed ? "h-10 w-10 justify-center" : "w-full px-2 py-1.5",
            )}
            aria-label="User menu"
          >
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/20 text-primary text-xs font-bold">
              {(user.username ?? user.email ?? "U").charAt(0).toUpperCase()}
            </div>
            {!isCollapsed && (
              <span className="overflow-hidden text-ellipsis whitespace-nowrap max-w-[130px]">
                {user.username ?? user.email}
              </span>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side={isCollapsed ? "right" : "top"} align="start" className="w-48">
          {showUpgrade && (
            <>
              <DropdownMenuItem
                className="p-1 focus:bg-transparent"
                onSelect={() => setShowPlanModal(true)}
              >
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  className="w-full justify-center whitespace-nowrap px-2"
                >
                  {t.nav.upgradeMyPlan}
                </Button>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          {uiMode.enabled && (
            <>
              <div className="px-2 pt-1.5 pb-1 text-[11px] text-muted-foreground">{t.lite.mode.title}</div>
              {(["lite", "advanced"] as const).map((m) => {
                const locked = m === "advanced" && !uiMode.canUseAdvanced;
                const Icon = m === "lite" ? Sparkles : Layers;
                return (
                  <DropdownMenuItem
                    key={m}
                    className={cn("flex items-center gap-3 cursor-pointer", uiMode.mode === m && "bg-accent")}
                    onSelect={() => (locked ? setShowPlanModal(true) : uiMode.setMode(m))}
                  >
                    <Icon size="1.1rem" />
                    <span className="flex-1">{m === "lite" ? t.lite.mode.lite : t.lite.mode.advanced}</span>
                    {uiMode.mode === m ? <Check className="size-3.5" /> : locked ? <Lock className="size-3.5 opacity-60" /> : null}
                  </DropdownMenuItem>
                );
              })}
              <DropdownMenuSeparator />
            </>
          )}
          {userMenuItems.map((item) => {
            if (item.isAdminOnly && !user.isAdmin) return null;
            // Prefer the i18n label when the item declares a translation key;
            // fall back to the English `name` otherwise.
            const labelKey = "labelKey" in item ? item.labelKey : undefined;
            const label = labelKey
              ? labelKey.split(".").reduce<any>((acc, k) => acc?.[k], t) ?? item.name
              : item.name;
            return (
              <DropdownMenuItem key={item.name} asChild>
                <WaspRouterLink to={item.to} className="flex w-full items-center gap-3 cursor-pointer">
                  <item.icon size="1.1rem" />
                  {label}
                </WaspRouterLink>
              </DropdownMenuItem>
            );
          })}
          <DropdownMenuItem>
            <button
              type="button"
              onClick={() => logout()}
              className="flex w-full items-center gap-3"
            >
              <LogOut size="1.1rem" />
              Log Out
            </button>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {showUpgrade && (
        <PlanSelectionModal open={showPlanModal} onOpenChange={setShowPlanModal} />
      )}
    </div>
  );
}
